import React, { useMemo, useState } from "react";
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Button,
} from "@fluentui/react-components";
import { ChevronDownRegular, ChevronUpRegular } from "@fluentui/react-icons";
import { ResponsiveLine } from "@nivo/line";
import { CFROIResponse } from "../types/api";

const useStyles = makeStyles({
  chartCard: {
    padding: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  chartTitle: {
    marginBottom: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  chartSubtitle: {
    marginBottom: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase200,
  },
  chartContainer: {
    height: "400px",
    width: "100%",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: tokens.spacingVerticalS,
  },
  titleSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  toggleButton: {
    minWidth: "auto",
    marginLeft: tokens.spacingHorizontalS,
    flexShrink: 0,
    fontSize: tokens.fontSizeBase100,
  },
});

interface PortfolioCashflowChartProps {
  cfroiData: CFROIResponse;
  paymentTerms: number;
}

const PortfolioCashflowChart: React.FC<PortfolioCashflowChartProps> = ({
  cfroiData,
  paymentTerms,
}) => {
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  const chartData = useMemo(() => {
    if (
      !cfroiData?.skuPaymentSeries ||
      cfroiData.skuPaymentSeries.length === 0
    ) {
      return [
        {
          id: "PO Cashflow",
          data: [{ x: 0, y: 0 }],
        },
      ];
    }

    // Calculate total investment excluding SKUs without sales data to match backend CFROI logic
    const totalInvestment = cfroiData.skuPaymentSeries.reduce(
      (sum, sku) => (sku.hasSalesData ? sum + (sku?.investment || 0) : sum),
      0
    );

    // Collect all payment entries from SKUs that have sales data
    const dailyRevenues = new Map<number, number>();

    cfroiData.skuPaymentSeries.forEach((sku) => {
      if (!sku?.paymentSeries || !sku.hasSalesData) return;

      // Process B2C entries with safety checks
      if (
        sku.paymentSeries.b2cEntries &&
        Array.isArray(sku.paymentSeries.b2cEntries)
      ) {
        sku.paymentSeries.b2cEntries.forEach((entry) => {
          if (
            entry &&
            typeof entry.period === "number" &&
            typeof entry.revenue === "number"
          ) {
            const currentRevenue = dailyRevenues.get(entry.period) || 0;
            dailyRevenues.set(entry.period, currentRevenue + entry.revenue);
          }
        });
      }

      // Process B2B entries with safety checks
      if (
        sku.paymentSeries.b2bEntries &&
        Array.isArray(sku.paymentSeries.b2bEntries)
      ) {
        sku.paymentSeries.b2bEntries.forEach((entry) => {
          if (
            entry &&
            typeof entry.period === "number" &&
            typeof entry.revenue === "number"
          ) {
            const currentRevenue = dailyRevenues.get(entry.period) || 0;
            dailyRevenues.set(entry.period, currentRevenue + entry.revenue);
          }
        });
      }
    });

    // Ensure payment terms day is included even if no revenue on that day
    if (!dailyRevenues.has(paymentTerms)) {
      dailyRevenues.set(paymentTerms, 0);
    }

    // Sort days and calculate accumulated cashflow
    const sortedDays = Array.from(dailyRevenues.keys())
      .filter((day) => typeof day === "number" && !isNaN(day))
      .sort((a, b) => a - b);

    let accumulatedCashflow = 0;
    const cashflowData: Array<{ x: number; y: number }> = [];

    // Add initial point at day 0 with 0 cashflow (only if payment isn't due on day 0)
    if (paymentTerms !== 0) {
      cashflowData.push({ x: 0, y: 0 });
    }

    sortedDays.forEach((day) => {
      const dailyRevenue = dailyRevenues.get(day) || 0;

      // On the payment terms day, subtract the total investment
      if (day === paymentTerms) {
        accumulatedCashflow += dailyRevenue - totalInvestment;
      } else {
        accumulatedCashflow += dailyRevenue;
      }

      // Ensure values are valid numbers
      const validX = typeof day === "number" && !isNaN(day) ? day : 0;
      const validY =
        typeof accumulatedCashflow === "number" && !isNaN(accumulatedCashflow)
          ? accumulatedCashflow
          : 0;

      cashflowData.push({ x: validX, y: validY });
    });

    return [
      {
        id: "Portfolio Cashflow",
        data: cashflowData,
      },
    ];
  }, [cfroiData, paymentTerms]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const customTooltip = ({ point }: any) => {
    const day = point.data.x;
    const cashflow = point.data.y;
    const isPaymentDay = day === paymentTerms;

    return (
      <div
        style={{
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          border: `1px solid ${tokens.colorNeutralStroke1}`,
          borderRadius: tokens.borderRadiusMedium,
          padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
          fontSize: tokens.fontSizeBase200,
          boxShadow: tokens.shadow8,
          minWidth: "280px",
          whiteSpace: "nowrap",
        }}
      >
        <div
          style={{
            fontWeight: tokens.fontWeightSemibold,
            marginBottom: tokens.spacingVerticalXS,
            display: "flex",
            alignItems: "center",
            gap: tokens.spacingHorizontalXS,
          }}
        >
          <span>Day {day}</span>
          {isPaymentDay && (
            <span
              style={{
                color: tokens.colorPaletteRedForeground1,
                backgroundColor: tokens.colorPaletteRedBackground1,
                padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
                borderRadius: tokens.borderRadiusSmall,
                fontSize: tokens.fontSizeBase100,
              }}
            >
              Payment Due
            </span>
          )}
        </div>
        <div
          style={{
            color: tokens.colorNeutralForeground2,
            marginBottom: isPaymentDay ? tokens.spacingVerticalXS : 0,
          }}
        >
          Accumulated Cashflow:{" "}
          <strong style={{ color: tokens.colorNeutralForeground1 }}>
            {formatCurrency(cashflow)}
          </strong>
        </div>
        {isPaymentDay && (
          <div
            style={{
              color: tokens.colorPaletteRedForeground1,
              fontSize: tokens.fontSizeBase100,
              fontStyle: "italic",
            }}
          >
            Investment payment of {formatCurrency(totalInvestment)} due today
          </div>
        )}
      </div>
    );
  };

  const totalInvestment =
    cfroiData?.skuPaymentSeries?.reduce(
      (sum, sku) => sum + (sku?.investment || 0),
      0
    ) || 0;

  const chartTheme = {
    background: "transparent",
    text: {
      fontSize: 12,
      fill: tokens.colorNeutralForeground1,
    },
    axis: {
      domain: {
        line: {
          stroke: tokens.colorNeutralStroke1,
          strokeWidth: 1,
        },
      },
      legend: {
        text: {
          fontSize: 12,
          fill: tokens.colorNeutralForeground1,
        },
      },
      ticks: {
        line: {
          stroke: tokens.colorNeutralStroke1,
          strokeWidth: 1,
        },
        text: {
          fontSize: 11,
          fill: tokens.colorNeutralForeground2,
        },
      },
    },
    grid: {
      line: {
        stroke: tokens.colorNeutralStroke2,
        strokeWidth: 1,
      },
    },
  };

  // Don't render if no valid data
  if (
    !chartData ||
    chartData.length === 0 ||
    !chartData[0]?.data ||
    chartData[0].data.length === 0
  ) {
    return (
      <Card className={styles.chartCard}>
        <Text className={styles.chartTitle}>PO Cashflow Analysis</Text>
        <Text className={styles.chartSubtitle}>
          No cashflow data available for this order.
        </Text>
      </Card>
    );
  }

  return (
    <Card className={styles.chartCard}>
      <div className={styles.headerRow}>
        <div className={styles.titleSection}>
          <Text className={styles.chartTitle}>PO Cashflow Analysis</Text>
          <Text className={styles.chartSubtitle}>
            Shows accumulated cashflow over time. The investment payment occurs
            on day {paymentTerms} (payment terms), causing the cashflow to drop
            by {formatCurrency(totalInvestment)} before recovering through
            continued sales.
          </Text>
        </div>
        <Button
          appearance="subtle"
          className={styles.toggleButton}
          icon={isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Hide" : "Show"}
        </Button>
      </div>

      {isExpanded && (
        <div className={styles.chartContainer}>
          <ResponsiveLine
            data={chartData}
            theme={chartTheme}
            margin={{ top: 20, right: 20, bottom: 60, left: 100 }}
            xScale={{ type: "linear", min: "auto", max: "auto" }}
            yScale={{ type: "linear", min: "auto", max: "auto" }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "Days",
              legendOffset: 36,
              legendPosition: "middle",
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "Accumulated Cashflow (€)",
              legendOffset: -80,
              legendPosition: "middle",
            }}
            pointSize={4}
            pointColor={{ theme: "background" }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            useMesh={true}
            enableGridX={true}
            enableGridY={true}
            colors={[tokens.colorPaletteBlueBorderActive]}
            curve="monotoneX"
            tooltip={customTooltip}
            markers={[
              {
                axis: "y",
                value: 0,
                lineStyle: {
                  stroke: tokens.colorNeutralForeground3,
                  strokeWidth: 2,
                  strokeDasharray: "12,4",
                },
              },
              ...(paymentTerms >= 0
                ? [
                    {
                      axis: "x" as const,
                      value: paymentTerms,
                      lineStyle: {
                        stroke: tokens.colorPaletteRedBackground3,
                        strokeWidth: 2,
                        strokeDasharray: "5,5",
                      },
                      legend: `Payment Due (Day ${paymentTerms})`,
                      legendOrientation: "vertical" as const,
                      legendPosition: "top-right" as const,
                      textStyle: {
                        fill: tokens.colorPaletteRedForeground1,
                        fontSize: 11,
                        fontWeight: 600,
                      },
                    },
                  ]
                : []),
            ]}
          />
        </div>
      )}
    </Card>
  );
};

export default PortfolioCashflowChart;
